import { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  fetchPricingExceptionsForLoan,
  createPricingException,
  approvePricingException,
  rejectPricingException,
  reRequestPricingException,
  uploadPricingExceptionDocument,
  findBuyDownOptionForRate,
  LegacyPricingExceptionDoc,
  PricingExceptionEmailRequestBody,
} from "../../../../../../utils/pricingException";

import { LoansContext } from "../../../../../../context/LoansContext";
import { RootContext } from "../../../../../../context/RootContext";

import RequestWaiver from "./RequestWaivers";

import "./RequestWaiverWrapper.css";
import {
  formatCurrency,
  validateNumberField,
} from "../../../../../../utils/v2/common";
import { getCookie, cleanedAdressString } from "../../../../../../utils/Common";
import { SizingTypeValue } from "../../../../../../types/enums";

// Module-level (not a useRef) — something re-mounts RequestWaiverWrapper
// entirely every ~1-2s while the Request Waiver tab is open (confirmed via
// diagnostic logging: a useRef guard kept resetting to its initial value,
// which only happens on a genuine unmount+remount, not a re-render), so a
// per-instance ref can't prevent the mount effect from refetching on every
// fresh instance. This survives across those remounts; it only refetches
// when the loanId actually changes.
let lastFetchedWaiversLoanId: string | undefined;

export default function RequestWaiverWrapper(props: any) {
  const { handleSaveApplication } = props;
  const params = useParams();
  const {
    buyDownOptions,
    currentSelectedInterestRate,
    setCurrentSelectedInterestRate,
    leverageAndPricingData,
    setLeverageAndPricingData,
    detailedLoanSizingData,
    loanDetails,
    quickLoanSizingData,
    setChipsData,
    chipsData,
    setWaiverRequestData,
    assignedPartner,
    eligiblePartners,
  } = useContext(LoansContext);
  const { decodedTokenData } = useContext(RootContext);

  const [isRequestPricingWaiver, setIsRequestPricingWaiver] = useState(false);
  const [requestedWaivers, setRequestedWaivers] = useState<LegacyPricingExceptionDoc[]>([]);
  const [waiverHistory, setWaiverHistory] = useState<LegacyPricingExceptionDoc[]>([]);

  const [comments, setComments] = useState("");
  const [document, setDocument] = useState<any>(null);
  const [documentPath, setDocumentPath] = useState("");
  const [userAction, setUserAction] = useState("");
  const [updatedDiscountPoints, setUpdatedDiscountPoints] = useState<any>({});
  const [approvalComment, setApprovalComment] = useState("");
  const [rejectionComment, setRejectionComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<any>({});

  // Derives, for a single legacy-shaped doc, the discount the borrower
  // actually ends up with once the exception is factored in — the original
  // buy-down option's discount minus whatever's been granted/requested,
  // unless rejected (in which case the borrower keeps the original,
  // unmodified discount). Identical for both the active card and each
  // history entry, so it's computed the same way for both below.
  const withPriceToBorrower = (item: LegacyPricingExceptionDoc) => {
    const discountPointsToBeDeducted =
      item?.data?.finalData?.discountPoints ||
      item?.data?.requestedData?.discountPoints;
    const discountPointAmountToBeDeducted = (
      item?.data?.finalData?.discountPointsAmount ||
      item?.data?.requestedData?.discountPointsAmount
    )?.replace(/[$,]/g, "");

    const discountPoints =
      +(item?.data?.actualData?.discountPoints ?? 0) -
      (item?.data?.waiverStatus === "Rejected" ? 0 : +(discountPointsToBeDeducted ?? 0));
    const formatDiscountPoints: any =
      discountPoints && !isNaN(+discountPoints) ? discountPoints.toFixed(4) : discountPoints;
    const discountPointsAmount =
      +(item?.data?.actualData?.discountPointsAmount ?? 0) -
      (item?.data?.waiverStatus === "Rejected" ? 0 : +(discountPointAmountToBeDeducted ?? 0));
    const formatDiscountPointsAmount: any =
      discountPointsAmount && !isNaN(+discountPointsAmount) ? discountPointsAmount.toFixed(2) : discountPointsAmount;

    return {
      ...item,
      data: {
        ...item.data,
        priceToBorrower: {
          interestRate: item?.data?.actualData?.interestRate,
          discountPoints: formatDiscountPoints <= 0 ? "0.0000" : formatDiscountPoints,
          discountPointsAmount: formatDiscountPointsAmount <= 0 ? "0.00" : formatDiscountPointsAmount,
        },
      },
    };
  };

  const currentResponseBy = (): string =>
    decodedTokenData?.name || decodedTokenData?.email || decodedTokenData?.user_id || "unknown";

  // Sent on every create/re-request/approve/reject call so the waiver
  // service can populate the exception-decision notification email.
  const buildEmailRequestBody = (): PricingExceptionEmailRequestBody => ({
    orgId: loanDetails?.orgId || getCookie("orgId"),
    evaluationId: loanDetails?.evaluationID,
    originalLoanAmount:
      loanDetails?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
        ? quickLoanSizingData?.loanInformation?.borrowerRequested
        : detailedLoanSizingData?.loanEconomics?.borrowerRequested,
    propertyAddress: cleanedAdressString(
      detailedLoanSizingData?.propertyDetails?.[0]?.propertyLocation ?? quickLoanSizingData?.propertyLocation
    ),
    originationPoints: leverageAndPricingData?.leverageCards?.originationPoints,
    totalBuyYield: currentSelectedInterestRate?.totalBuyYield?.toString(),
    createdByEmail: decodedTokenData?.email,
  });

  const handleWaiverRequest = async (file: File | null) => {
    setIsLoading(true);
    try {
      await handleSaveApplication(false, "", "", null, true);
      // findBuyDownOptionForRate does a rounded-string comparison, not a
      // strict === on floats — buyDownOptions' interestRate is a fraction
      // computed via a different arithmetic path than the string-parsed
      // percentage here, so a raw === essentially never matched, and this
      // lookup silently fell through to {} on every request, meaning
      // requestedDiscountPointPercentage/Amount (below) never actually
      // reflected the selected buy-down option's real discount values.
      const selectedBuyDownOption =
        findBuyDownOptionForRate(
          buyDownOptions,
          currentSelectedInterestRate?.interestRate,
        ) ?? {};

      // Two distinct values, two distinct destinations:
      //  - actualDiscountPoints/Amount: the ACTUAL selected buy-down
      //    option's discount values — go only onto the waiver record itself
      //    (requestedDiscountPointPercentage/Amount in the create payload).
      //  - discountPoints/discountPointsAmount: whatever the user actually
      //    typed into the request form's editable discount fields — go onto
      //    the POST .../response call, both the REQUESTED response
      //    createPricingException posts right after creation, and any
      //    later re-request.
      const actualDiscountPoints =
        selectedBuyDownOption?.discountPointPercentage ||
        currentSelectedInterestRate?.discountPoints;

      const actualDiscountPointsAmount =
        selectedBuyDownOption?.discountPointAmount ||
        currentSelectedInterestRate?.discountPointsWithDollar?.replace(
          /[^0-9.]/g,
          ""
        );

      const discountPoints =
        updatedDiscountPoints?.discountPoints ||
        currentSelectedInterestRate?.discountPoints;

      const discountPointsAmount =
        updatedDiscountPoints?.discountPointsWithDollar?.replace(
          /[^0-9.]/g,
          ""
        ) ||
        currentSelectedInterestRate?.discountPointsWithDollar?.replace(
          /[^0-9.]/g,
          ""
        );

      const fields = {
        discountPoints: discountPoints ? `${parseFloat(discountPoints)?.toFixed(4)}` : discountPoints,
        discountPointsAmount: discountPointsAmount
          ? `${parseFloat(discountPointsAmount)?.toFixed(2)}`
          : discountPointsAmount,
        comments,
        emailRequestBody: buildEmailRequestBody(),
      };

      // A pending (not-yet-decided) exception already exists on this loan —
      // this is a re-request, so it's recorded as a new response on that
      // same record rather than a brand new one (see ReRequestWaiverConfirmation).
      const existingPendingWaiverId = requestedWaivers?.[0]?._id;
      if (existingPendingWaiverId) {
        await reRequestPricingException(existingPendingWaiverId, currentResponseBy(), fields);
        if (file) {
          await uploadPricingExceptionDocument(existingPendingWaiverId, file);
        }
      } else {
        await createPricingException(
          params?.loanId,
          currentResponseBy(),
          {
            interestRate:
              updatedDiscountPoints?.interestRate || currentSelectedInterestRate?.interestRate,
            ...fields,
            actualDiscountPoints: actualDiscountPoints
              ? `${parseFloat(actualDiscountPoints)?.toFixed(4)}`
              : actualDiscountPoints,
            actualDiscountPointsAmount: actualDiscountPointsAmount
              ? `${parseFloat(actualDiscountPointsAmount)?.toFixed(2)}`
              : actualDiscountPointsAmount,
          },
          file,
          assignedPartner || eligiblePartners?.[0] || null,
        );
      }
      const tempChipsData = chipsData?.filter(
        (chip: string) => !chip.includes("Price Exception")
      );
      setChipsData([...tempChipsData, "Price Exception: Requested"]);
      fetchWaivers();
      setDocument(null);
      setComments("");
      setIsRequestPricingWaiver(false);
      setUpdatedDiscountPoints({});
      handleSaveApplication(false, "", "waiverRequested");
    } catch (err) {
      console.error(
        "Something went wrong while requesting waiver. ERROR:",
        err
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAction = (action: string) => {
    setUserAction(action);
  };

  const fetchWaivers = async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchPricingExceptionsForLoan(
        params.loanId,
        buyDownOptions,
        currentSelectedInterestRate?.interestRate,
      );

      const withIndex = data?.data?.map((item, index) => ({ ...item, originalIndex: index })) ?? [];
      const activeWaiver = withIndex.filter((item) => item?.data?.isActive);
      const sortedData = withIndex
        .filter((item) => !item?.data?.isActive)
        .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());

      setRequestedWaivers(activeWaiver.map(withPriceToBorrower));
      setWaiverHistory(sortedData.map(withPriceToBorrower));
      // Shared with LoansPagev2's updateChips(), which reads this same
      // context state to render the "Price Exception: <status>" chip —
      // setWaiverRequestData was previously never called anywhere, so that
      // chip could never appear. Reuses this already-scheduled fetch
      // (mount + after every create/approve/reject) instead of adding a
      // separate one just for the chip.
      setWaiverRequestData(withIndex);
    } catch (err) {
      console.error("Something went wrong while fetching waivers. ERROR:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscountPointChange = (discountValue: any) => {
    const { value, type, key } = discountValue;
    if (value) {
      setUpdatedDiscountPoints({
        ...currentSelectedInterestRate,
        [key]: value,
      });
      // setCurrentSelectedInterestRate({
      //   ...currentSelectedInterestRate,
      //   [key]: value,
      // });
    } else {
      const returnObj = {
        ...currentSelectedInterestRate,
        discountPoints: "",
        discountPointsWithDollar: "",
      };
      // setCurrentSelectedInterestRate({
      //   ...returnObj,
      // });
      setUpdatedDiscountPoints({
        ...currentSelectedInterestRate,
        returnObj,
      });
    }
  };

  const validateInput = (value: any, key: any) => {
    let isError = false;
    const sanitizedValue = value.replace(/[\s,$%]/g, "");
    if (
      !sanitizedValue ||
      +sanitizedValue < 0 ||
      validateNumberField(sanitizedValue)
    ) {
      setFieldErrors({
        ...fieldErrors,
        [key]: "pleaseEnterValidValue",
      });
      isError = true;
    }
    return isError;
  };

  const handleDiscountBlur = (discountValue: any) => {
    const { value, type, key } = discountValue;

    const isError = validateInput(value, key);
    if (isError) {
      const returnObj = {
        ...currentSelectedInterestRate,
        discountPoints: "",
        discountPointsWithDollar: "",
      };
      setUpdatedDiscountPoints({
        ...returnObj,
      });
      // setCurrentSelectedInterestRate({
      //   ...returnObj,
      // });
      return;
    }

    const data =
      loanDetails?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
        ? quickLoanSizingData?.loanInformation?.borrowerRequested
        : detailedLoanSizingData?.loanEconomics?.borrowerRequested;
    const tempValue = value?.replace(/[$,]/g, "");
    if (tempValue) {
      const newValue: any =
        type === "percentage"
          ? `${formatCurrency(
              (tempValue?.replace(/[$,]/g, "") * +data) / 100,
              2
            )}`
          : (+tempValue?.replace(/[$,]/g, "") / +data) * 100;

      const returnObj = {
        ...currentSelectedInterestRate,
        discountPoints:
          key === "discountPoints"
            ? `${parseFloat(tempValue)?.toFixed(4)}`
            : newValue?.toFixed(4),
        discountPointsWithDollar:
          key === "discountPointsWithDollar"
            ? tempValue?.includes("$")
              ? tempValue
              : formatCurrency(tempValue, 2)
            : newValue,
      };
      setUpdatedDiscountPoints({
        ...returnObj,
      });
      // setCurrentSelectedInterestRate({
      //   ...returnObj,
      // });
    } else {
      const returnObj = {
        ...currentSelectedInterestRate,
        discountPoints: "",
        discountPointsWithDollar: "",
      };
      setUpdatedDiscountPoints({
        ...returnObj,
      });
      // setCurrentSelectedInterestRate({
      //   ...currentSelectedInterestRate,
      // });
    }
    setFieldErrors({});
  };

  const handleApprovalComment = (e: any) => {
    setApprovalComment(e.target.value);
  };

  const handleRejectionComment = (e: any) => {
    setRejectionComment(e.target.value);
  };

  const handleApproveWaiver = async (props: any) => {
    const { _id } = props;

    setIsLoading(true);
    try {
      await approvePricingException(_id, currentResponseBy(), {
        discountPoints:
          updatedDiscountPoints?.discountPoints ||
          props?.data?.requestedData?.discountPoints ||
          currentSelectedInterestRate?.discountPoints,
        discountPointsAmount:
          updatedDiscountPoints?.discountPointsWithDollar ||
          props?.data?.requestedData?.discountPointsAmount ||
          currentSelectedInterestRate?.discountPointsWithDollar,
        comments: approvalComment,
        emailRequestBody: buildEmailRequestBody(),
      });
      const tempChipsData = chipsData?.filter(
        (chip: string) => !chip.includes("Price Exception")
      );
      setChipsData([...tempChipsData, `Price Exception: Approved`]);
      setCurrentSelectedInterestRate({
        ...currentSelectedInterestRate,
        discountPoints:
          updatedDiscountPoints?.discountPoints ||
          props?.data?.requestedData?.discountPoints,
        discountPointsWithDollar:
          updatedDiscountPoints?.discountPointsWithDollar ||
          props?.data?.requestedData?.discountPointsAmount,
      });
      fetchWaivers();
      // Unlike handleInterestChange (which explicitly passes the selected
      // rate through), approving an exception was leaving
      // handleSaveApplication to persist whatever loanEconomics.interestRate
      // already happened to hold — which can have drifted (e.g.
      // clearStalePricingOverrides resets it on any unrelated field edit
      // made between selecting the buy-down and approving the exception
      // raised against it). Passing currentSelectedInterestRate explicitly
      // re-asserts the rate this exception was actually approved for, so
      // reopening the loan later rehydrates the right one.
      handleSaveApplication(false, "", "waiverApproved", currentSelectedInterestRate);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      setRejectionComment("");
    }
  };

  const handleRejectionWaiver = async (props: any) => {
    const { _id } = props;

    if (!rejectionComment) {
      // alert("Please enter rejection comment.");
      return;
    }

    setIsLoading(true);
    try {
      await rejectPricingException(_id, currentResponseBy(), {
        discountPoints:
          props?.data?.requestedData?.discountPoints ||
          currentSelectedInterestRate?.discountPoints,
        discountPointsAmount:
          props?.data?.requestedData?.discountPointsAmount ||
          currentSelectedInterestRate?.discountPointsWithDollar,
        comments: rejectionComment,
        emailRequestBody: buildEmailRequestBody(),
      });
      const tempChipsData = chipsData?.filter(
        (chip: string) => !chip.includes("Price Exception")
      );
      setChipsData([...tempChipsData, `Price Exception: Rejected`]);
      fetchWaivers();
      handleSaveApplication(false, "", "waiverRejected");
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelApproveWaiver = () => {
    const findCurrentSelection = buyDownOptions?.find(
      (option: any) =>
        option.interestRate ===
        Number(
          currentSelectedInterestRate?.interestRate?.replace("%", "") / 100
        )
    );

    setCurrentSelectedInterestRate({
      interestRate: `${(findCurrentSelection?.interestRate * 100)?.toFixed(
        3
      )}%`,
      discountPoints: `${findCurrentSelection?.discountPointPercentage?.toFixed(
        4
      )}`,
      discountPointsWithDollar: formatCurrency(
        findCurrentSelection?.discountPointAmount,
        2
      ),
      totalBuyYield: findCurrentSelection?.totalBuyYield,
    });

    setIsLoading(true);
    setUserAction("");
    setApprovalComment("");
    setRejectionComment("");
    setIsLoading(false);
    setUpdatedDiscountPoints({});
  };

  // Guards against re-fetching for a loanId this component (or an earlier,
  // now-unmounted instance of it — see lastFetchedWaiversLoanId above) has
  // already fetched. The three action handlers (create/approve/reject) call
  // fetchWaivers() directly, outside this effect, so they're unaffected and
  // still refetch on every real action.
  useEffect(() => {
    if (lastFetchedWaiversLoanId === params.loanId) return;
    lastFetchedWaiversLoanId = params.loanId;
    fetchWaivers();
  }, []);

  useEffect(() => {
    setFieldErrors({});
  }, [currentSelectedInterestRate?.interestRate]);

  return (
    <div className="ww-100" style={{ marginTop: "10px" }}>
      <RequestWaiver
        currentSelectedInterestRate={currentSelectedInterestRate}
        comments={comments}
        setComments={setComments}
        requestedWaivers={requestedWaivers}
        isRequestPricingWaiver={isRequestPricingWaiver}
        setRequestedWaivers={setRequestedWaivers}
        setIsRequestPricingWaiver={setIsRequestPricingWaiver}
        handleWaiverRequest={handleWaiverRequest}
        document={document}
        setDocument={setDocument}
        documentPath={documentPath}
        setDocumentPath={setDocumentPath}
        userAction={userAction}
        handleUserAction={handleUserAction}
        handleDiscountPointChange={handleDiscountPointChange}
        updatedDiscountPoints={updatedDiscountPoints}
        handleDiscountBlur={handleDiscountBlur}
        handleApprovalComment={handleApprovalComment}
        approvalComment={approvalComment}
        handleApproveWaiver={handleApproveWaiver}
        cancelApproveWaiver={cancelApproveWaiver}
        rejectionComment={rejectionComment}
        handleRejectionComment={handleRejectionComment}
        handleRejectionWaiver={handleRejectionWaiver}
        fetchWaivers={fetchWaivers}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        waiverHistory={waiverHistory}
        fieldErrors={fieldErrors}
        buyDownOptions={buyDownOptions}
        leverageAndPricingData={leverageAndPricingData}
        isLoanSubmitted={Boolean(loanDetails?.tcLoanId)}
      />
    </div>
  );
}
